import React, { useState } from 'react';
import { toast } from 'sonner';
import { Receipt, AlertOctagon, Download, Loader, RotateCcw, Undo2 } from 'lucide-react';
import apiClient from '../../utils/apiClient';

/**
 * BillingHistorySection — ESS parity on the Customer detail page.
 *
 * Renders the chronological ledger merge that the GET /customers/{id}
 * endpoint denormalizes server-side: invoices (charges), payments
 * (credits), refunds + voids (negative credits / negative charges),
 * each row stamped with the running balance AFTER the event.
 *
 * Pure render component — every field is materialized by the backend so
 * the frontend never reaches into the ledger directly. Newest-first.
 *
 * Defaults to showing the last `defaultRows` (10) entries with a "Full
 * history (N)" toggle to expand. The backend already capped the response
 * at the last 50 entries (billing_history_truncated flag); a v2 endpoint
 * can re-fetch the full ledger when that toggle is enabled.
 */

const fmtMoney = (n) => {
  const v = Number(n || 0);
  // Negative payments (refunds, voids) render with the sign so the
  // operator sees money going the other direction.
  if (v < 0) return `-$${Math.abs(v).toFixed(2)}`;
  return `$${v.toFixed(2)}`;
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!y || !m || !d) return s;
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y}`;
};

const rowBg = (row) => {
  // Color rules from spec:
  //   payment_amount > 0 (received) -> subtle emerald
  //   payment_amount < 0 or type=void -> subtle rose
  //   type=invoice -> neutral white
  if (row.type === 'void') return 'bg-rose-50/60';
  if (row.type === 'refund') return 'bg-rose-50/60';
  if (row.type === 'invoice_refund') return 'bg-rose-50/60';
  if (row.type === 'payment') {
    if (Number(row.payment_amount || 0) < 0) return 'bg-rose-50/60';
    return 'bg-emerald-50/60';
  }
  return 'bg-white';
};

const DEFAULT_VISIBLE = 10;

const BillingHistorySection = ({
  billingHistory = [],
  truncated = false,
  total = 0,
  onLoadFull,
  facilityId = null,
  customerId = null,
  creditBalance = 0,
  // Called after a successful void so the parent can re-fetch the customer /
  // ledger. Falls back to a page reload when not supplied.
  onChanged = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  // Granting credit is deliberate and separate from returning it: two steps,
  // two records, two reasons. Money can never leave on one click.
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);
  // Credit refund — returns money the ledger already says is theirs
  // (credit balance). Owner 2026-08-03: both refund doors live HERE on
  // the customer profile, not on the Payments page.
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);

  const refundCredit = async () => {
    const available = Number(creditBalance || 0);
    const value = parseFloat(refundAmount);
    if (!value || value <= 0) { toast.error('Enter an amount.'); return; }
    if (value > available) {
      toast.error(`Only $${available.toFixed(2)} credit is available to refund.`);
      return;
    }
    if (!refundReason || refundReason.trim().length < 3) {
      toast.error('A reason is required.'); return;
    }
    setRefundBusy(true);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/payments/refund-credit/${customerId}`,
        { amount: value, reason: refundReason.trim() },
      );
      toast.success(`Refunded $${value.toFixed(2)} of credit.`);
      setRefundOpen(false); setRefundAmount(''); setRefundReason('');
      refreshAfterChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not refund credit.');
    } finally {
      setRefundBusy(false);
    }
  };

  const addCredit = async () => {
    const value = parseFloat(creditAmount);
    if (!value || value <= 0) { toast.error('Enter an amount.'); return; }
    if (!creditReason || creditReason.trim().length < 3) {
      toast.error('A reason is required.'); return;
    }
    setCreditBusy(true);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/payments/add-credit/${customerId}`,
        { amount: value, reason: creditReason.trim() },
      );
      toast.success(`Added $${value.toFixed(2)} credit.`);
      setCreditOpen(false); setCreditAmount(''); setCreditReason('');
      if (onChanged) onChanged(); else window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add credit.');
    } finally {
      setCreditBusy(false);
    }
  };
  // Track which invoice_id is currently downloading so we can swap icons.
  const [downloadingId, setDownloadingId] = useState(null);
  const [voidingId, setVoidingId] = useState(null);
  const [massVoiding, setMassVoiding] = useState(false);

  const refreshAfterChange = () => {
    if (typeof onChanged === 'function') onChanged();
    else window.location.reload();
  };

  // Voidable = a real invoice that is NOT already void and NOT paid, with an
  // outstanding (unpaid) balance. Paid invoices are never voided from here —
  // reversing money already collected is a refund, a different operation.
  const isVoidable = (row) => {
    if (row.type !== 'invoice' || !row.invoice_id) return false;
    const st = String(row.status || '').toLowerCase();
    if (st === 'void' || st === 'paid') return false;
    const outstanding = Number(row.charge_amount || 0) - Number(row.amount_paid || 0);
    return outstanding > 0;
  };

  // Refundable = a paid (or partly paid) invoice: money came in against
  // it, so "refund this invoice" returns exactly that money AND voids
  // the charge in one action (the owner's mental model, 2026-08-03).
  const isInvoiceRefundable = (row) => {
    if (row.type !== 'invoice' || !row.invoice_id) return false;
    const st = String(row.status || '').toLowerCase();
    if (st === 'void') return false;
    return Number(row.amount_paid || 0) > 0;
  };

  const refundInvoice = async (row) => {
    if (!facilityId || !row?.invoice_id) return;
    const paid = Number(row.amount_paid || 0).toFixed(2);
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      `Refund $${paid} paid on ${row.item_number} and void the charge?\n`
      + 'The money goes back the way it was paid (card via Stripe, '
      + 'otherwise recorded as an external refund).\n\n'
      + 'Reason (required, goes on the audit record):');
    if (reason === null) return;
    if (!reason || reason.trim().length < 3) {
      // eslint-disable-next-line no-alert
      alert('A reason of at least 3 characters is required.');
      return;
    }
    setVoidingId(row.invoice_id);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/payments/refund-invoice/${row.invoice_id}`,
        { reason: reason.trim() },
      );
      refreshAfterChange();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Could not refund this invoice.');
    } finally {
      setVoidingId(null);
    }
  };

  const voidInvoice = async (invoiceId) => {
    if (!facilityId || !invoiceId) return;
    setVoidingId(invoiceId);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/invoices/${invoiceId}/void`,
      );
      refreshAfterChange();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Could not void this invoice.');
    } finally {
      setVoidingId(null);
    }
  };

  // Reverse a void — restores the invoice + re-adds its balance.
  const unvoidInvoice = async (invoiceId) => {
    if (!facilityId || !invoiceId) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Reverse this void? The invoice is restored and its '
      + 'balance is added back.')) return;
    setVoidingId(invoiceId);
    try {
      await apiClient.post(
        `/api/facilities/${facilityId}/invoices/${invoiceId}/unvoid`,
      );
      refreshAfterChange();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail || 'Could not reverse this void.');
    } finally {
      setVoidingId(null);
    }
  };

  const handleDownloadInvoice = async (row) => {
    if (!facilityId || !row?.invoice_id) return;
    setDownloadingId(row.invoice_id);
    try {
      const res = await apiClient.get(
        `/api/facilities/${facilityId}/invoices/${row.invoice_id}/pdf`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const fname = row.item_number || row.invoice_id;
      a.download = `invoice-${fname}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err.response?.data?.detail || 'Could not download invoice PDF.');
    } finally {
      setDownloadingId(null);
    }
  };

  if (!billingHistory.length) {
    return (
      <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
          <Receipt className="h-4 w-4 text-slate-500" />
          <div>
            <h3 className="text-[14px] font-semibold text-slate-900">Billing History</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Invoices, payments, refunds and voids for this customer.
            </p>
          </div>
        </div>
        <div className="px-5 py-6 text-center text-[13px] text-slate-400">
          No billing history yet.
        </div>
      </div>
    );
  }

  const visibleRows = expanded ? billingHistory : billingHistory.slice(0, DEFAULT_VISIBLE);
  const hasMoreLocally = billingHistory.length > DEFAULT_VISIBLE;

  const voidableInvoices = billingHistory.filter(isVoidable);

  const handleMassVoid = async () => {
    if (!facilityId || voidableInvoices.length === 0) return;
    const totalToVoid = voidableInvoices.reduce(
      (s, r) => s + Number(r.charge_amount || 0), 0,
    );
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Void ${voidableInvoices.length} invoice(s) totaling `
      + `$${totalToVoid.toFixed(2)}? This reverses their balance and cannot `
      + `be undone.`,
    )) return;
    setMassVoiding(true);
    try {
      // Void sequentially so one failure doesn't abort the rest silently.
      for (const inv of voidableInvoices) {
        // eslint-disable-next-line no-await-in-loop
        await apiClient.post(
          `/api/facilities/${facilityId}/invoices/${inv.invoice_id}/void`,
        );
      }
      refreshAfterChange();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(e?.response?.data?.detail
        || 'Some invoices could not be voided. Please refresh and retry.');
    } finally {
      setMassVoiding(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg mb-4 overflow-hidden">
      <div className="px-5 py-3.5 border-b border-slate-100 flex items-center gap-3">
        <Receipt className="h-4 w-4 text-slate-500" />
        <div className="flex-1">
          <h3 className="text-[14px] font-semibold text-slate-900">
            Billing History
            <span className="ml-2 text-[11px] font-medium text-slate-400">
              ({total || billingHistory.length})
            </span>
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Chronological ledger — newest first. Running balance per row.
          </p>
        </div>
        {/* Credit lives here because it is money, not a profile field. It
            offsets the next invoice, or the Refund button beside returns it. */}
        {Number(creditBalance) > 0 && (
          <span className="text-[12px] px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 whitespace-nowrap">
            Credit ${Number(creditBalance).toFixed(2)}
          </span>
        )}
        {facilityId && customerId && (
          <button
            type="button"
            onClick={() => setCreditOpen(true)}
            title="Put credit on this account — refunding it is a separate, second step"
            className="text-[12px] font-medium px-2.5 py-1 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors whitespace-nowrap"
          >
            Add credit
          </button>
        )}
        {facilityId && customerId && (
          <button
            type="button"
            onClick={() => setRefundOpen(true)}
            title="Return credit-balance money to the customer. To refund a specific charge, use the refund action on that invoice line below."
            className="text-[12px] font-medium px-2.5 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            Refund
          </button>
        )}
      </div>

      {creditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
             onClick={() => setCreditOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
              Add credit
            </h3>
            <p className="text-[12px] text-slate-500 mb-3">
              Puts credit on this account. It offsets their next invoice, or
              the <strong>Refund</strong> button here returns it.
              Adding and returning are separate steps on purpose — money never
              leaves on one click.
            </p>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Amount</label>
            <input type="number" min="0" step="0.01"
                   className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                   value={creditAmount}
                   onChange={(e) => setCreditAmount(e.target.value)} />
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Reason</label>
            <input type="text" placeholder="e.g. goodwill for the gate outage"
                   className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                   value={creditReason}
                   onChange={(e) => setCreditReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setCreditOpen(false)}
                      className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300">
                Cancel
              </button>
              <button type="button" onClick={addCredit} disabled={creditBusy}
                      className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-[13px] font-medium hover:bg-emerald-700 disabled:opacity-50">
                {creditBusy ? 'Adding…' : 'Add credit'}
              </button>
            </div>
          </div>
        </div>
      )}
      {refundOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
             onClick={() => setRefundOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[14px] font-semibold text-slate-900 mb-1">
              Refund credit
            </h3>
            {Number(creditBalance || 0) > 0 ? (
              <p className="text-[12px] text-slate-500 mb-3">
                Returns credit-balance money —
                <strong> ${Number(creditBalance).toFixed(2)}</strong> is
                available. To refund a specific charge (like an NSF fee),
                use the refund action on that invoice line in the ledger
                below instead.
              </p>
            ) : (
              <p className="text-[12px] text-amber-600 mb-3">
                No credit balance on this account. To return a charge the
                customer paid (like an NSF fee), use the refund action on
                that invoice line in the ledger below — it returns the
                money and voids the charge in one step.
              </p>
            )}
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Amount</label>
            <input type="number" min="0" step="0.01"
                   className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                   value={refundAmount}
                   disabled={Number(creditBalance || 0) <= 0}
                   onChange={(e) => setRefundAmount(e.target.value)} />
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Reason</label>
            <input type="text" placeholder="e.g. returning move-out credit"
                   className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] mb-3"
                   value={refundReason}
                   disabled={Number(creditBalance || 0) <= 0}
                   onChange={(e) => setRefundReason(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setRefundOpen(false)}
                      className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300">
                Cancel
              </button>
              <button type="button" onClick={refundCredit}
                      disabled={refundBusy || Number(creditBalance || 0) <= 0}
                      className="px-3.5 py-2 rounded-lg bg-amber-600 text-white text-[13px] font-medium hover:bg-amber-700 disabled:opacity-50">
                {refundBusy ? 'Refunding…' : 'Refund'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-[13px]">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">Date</th>
              <th className="text-left font-medium px-4 py-2.5 whitespace-nowrap">Item</th>
              <th className="text-left font-medium px-4 py-2.5">Description</th>
              <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">Charges</th>
              <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">Payments/Voids</th>
              <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">Balance</th>
              <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {visibleRows.map((row, idx) => (
              <tr
                key={`${row.item_number || 'r'}-${idx}`}
                className={`${rowBg(row)} hover:bg-slate-100/40`}
              >
                <td className="px-4 py-2.5 text-slate-700 whitespace-nowrap">
                  {fmtDate(row.date)}
                </td>
                <td className="px-4 py-2.5 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                  {row.item_number || '—'}
                </td>
                <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md">
                  <span className="block truncate" title={row.description || ''}>
                    {row.description || '—'}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {row.type === 'invoice' && Number(row.charge_amount || 0) !== 0 ? (
                    <span className="text-slate-900 font-medium">
                      {fmtMoney(row.charge_amount)}
                    </span>
                  ) : row.type === 'void' || row.type === 'invoice_refund' ? (
                    <span className="text-rose-700 font-medium">
                      {fmtMoney(row.charge_amount)}
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  {row.type === 'payment' || row.type === 'refund'
                    || row.type === 'invoice_refund' ? (
                    Number(row.payment_amount || 0) < 0 ? (
                      <span className="text-rose-700 font-medium">
                        {fmtMoney(row.payment_amount)}
                      </span>
                    ) : (
                      <span className="text-emerald-700 font-medium">
                        {fmtMoney(row.payment_amount)}
                      </span>
                    )
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right text-slate-900 font-semibold whitespace-nowrap">
                  {fmtMoney(row.balance_after)}
                </td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1 justify-end">
                    {/* Selective void: only on an unpaid invoice row. */}
                    {isVoidable(row) && facilityId ? (
                      <button
                        type="button"
                        onClick={() => voidInvoice(row.invoice_id)}
                        disabled={voidingId === row.invoice_id || massVoiding}
                        title="Void this invoice"
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-rose-100 text-rose-500 hover:text-rose-700 disabled:opacity-50 transition-colors"
                      >
                        {voidingId === row.invoice_id ? (
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <AlertOctagon className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : null}
                    {/* One-step invoice refund: on a paid invoice row. */}
                    {isInvoiceRefundable(row) && facilityId ? (
                      <button
                        type="button"
                        onClick={() => refundInvoice(row)}
                        disabled={voidingId === row.invoice_id || massVoiding}
                        title={`Refund the $${Number(row.amount_paid || 0).toFixed(2)} paid on this invoice and void the charge`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-amber-100 text-amber-600 hover:text-amber-700 disabled:opacity-50 transition-colors"
                      >
                        {voidingId === row.invoice_id ? (
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Undo2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : null}
                    {/* Reverse a void: on a void row. */}
                    {row.type === 'void' && row.invoice_id && facilityId ? (
                      <button
                        type="button"
                        onClick={() => unvoidInvoice(row.invoice_id)}
                        disabled={voidingId === row.invoice_id}
                        title="Reverse this void (restore invoice)"
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-amber-100 text-amber-600 hover:text-amber-700 disabled:opacity-50 transition-colors"
                      >
                        {voidingId === row.invoice_id ? (
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : null}
                    {(row.type === 'invoice' || row.type === 'void'
                      || row.type === 'invoice_refund') && row.invoice_id && facilityId ? (
                      <button
                        type="button"
                        onClick={() => handleDownloadInvoice(row)}
                        disabled={downloadingId === row.invoice_id}
                        title="Download invoice PDF"
                        className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-slate-200/60 text-slate-500 hover:text-slate-800 disabled:opacity-50 transition-colors"
                      >
                        {downloadingId === row.invoice_id ? (
                          <Loader className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Download className="h-3.5 w-3.5" />
                        )}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50/40">
        <div className="flex items-center gap-3">
          {hasMoreLocally && (
            <button
              type="button"
              onClick={async () => {
                // When the server capped the ledger, pull the FULL history
                // before expanding (previously "older history archived" was
                // simply unavailable).
                if (!expanded && truncated && onLoadFull) await onLoadFull();
                setExpanded((v) => !v);
              }}
              className="text-[12px] font-medium text-blue-600 hover:text-blue-700 hover:underline"
            >
              {expanded
                ? 'Show recent only'
                : `Full history (${total || billingHistory.length})`}
            </button>
          )}

        </div>
        <button
          type="button"
          onClick={handleMassVoid}
          disabled={massVoiding || voidableInvoices.length === 0}
          title={voidableInvoices.length === 0
            ? 'No unpaid invoices to void'
            : `Void ${voidableInvoices.length} unpaid invoice(s)`}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 hover:text-rose-600 disabled:opacity-40 disabled:hover:text-slate-600 disabled:cursor-not-allowed"
        >
          {massVoiding
            ? <Loader className="h-3.5 w-3.5 animate-spin" />
            : <AlertOctagon className="h-3.5 w-3.5" />}
          Mass Void Unpaid Line Items
          {voidableInvoices.length > 0 ? ` (${voidableInvoices.length})` : ''}
        </button>
      </div>
    </div>
  );
};

export default BillingHistorySection;
